/**
 * AuthService - Framework-agnostic authentication service for MSAL.
 *
 * This is the PRIMARY auth interface for module federation.
 * It provides a framework-agnostic API that works with React, Angular, Vue, or vanilla JS.
 *
 * When auth is disabled via env.auth.disabled, this service returns mock values
 * that treat the user as authenticated (bypassing auth).
 */

import {
    PublicClientApplication,
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
    EventType,
    InteractionStatus,
    EventMessage
} from '@azure/msal-browser';
import { DEFAULT_LOGIN_SCOPES, type FederatedAuth } from '@platform-ui/auth';
import { msalConfig, isAuthDisabled } from './auth-config';

export type TokenRequest = {
    scopes: string[];
    authority?: string;
    account?: AccountInfo;
};

type AuthListener = (accounts: AccountInfo[]) => void;

/**
 * AuthService class that wraps MSAL and provides a clean API.
 * Handles both enabled and disabled auth modes.
 */
class AuthService {
    private listeners = new Set<AuthListener>();
    private _pca: PublicClientApplication | null = null;
    private _interactionStatus: InteractionStatus = InteractionStatus.Startup;
    private _isDisabled: boolean;
    private _initialized = false;
    private _requiresInteraction = false;

    constructor() {
        this._isDisabled = isAuthDisabled;
        if (this._isDisabled) {
            console.log('[AuthService] Auth is disabled via configuration');
        }
    }

    /**
     * Check if auth is disabled.
     */
    get disabled(): boolean {
        return this._isDisabled;
    }

    /**
     * Whether the user needs interactive re-authentication.
     * Set when acquireTokenSilent fails with InteractionRequiredAuthError,
     * cleared on successful login.
     */
    get requiresInteraction(): boolean {
        return this._requiresInteraction;
    }

    /**
     * Get the MSAL instance, lazily creating it if needed.
     * Throws if auth is disabled.
     */
    private get pca(): PublicClientApplication {
        if (this._isDisabled) {
            throw new Error('Auth is disabled. MSAL instance is not available.');
        }
        if (!this._pca) {
            this._pca = new PublicClientApplication(msalConfig);
            this.setupEventCallbacks();
        }
        return this._pca;
    }

    /**
     * Initialize the service with an existing MSAL instance.
     * Call this from main.tsx after creating the instance.
     */
    initialize(instance: PublicClientApplication): void {
        if (this._isDisabled) {
            console.log('[AuthService] Skipping initialization - auth is disabled');
            return;
        }
        if (this._pca) {
            console.warn('AuthService already initialized');
            return;
        }
        this._pca = instance;
        this._interactionStatus = InteractionStatus.None;
        this.setupEventCallbacks();
    }

    private setupEventCallbacks(): void {
        if (!this._pca) return;

        this._pca.addEventCallback((event: EventMessage) => {
            // Mark initialized when PCA is ready
            if (event.eventType === EventType.INITIALIZE_END) {
                this._initialized = true;
                this.emit();
            }

            // Track interaction status
            if (event.eventType === EventType.LOGIN_START) {
                this._interactionStatus = InteractionStatus.Login;
            } else if (event.eventType === EventType.ACQUIRE_TOKEN_START) {
                this._interactionStatus = InteractionStatus.AcquireToken;
            } else if (event.eventType === EventType.SSO_SILENT_START) {
                this._interactionStatus = InteractionStatus.SsoSilent;
            } else if (event.eventType === EventType.HANDLE_REDIRECT_START) {
                this._interactionStatus = InteractionStatus.HandleRedirect;
            } else if (event.eventType === EventType.LOGOUT_START) {
                this._interactionStatus = InteractionStatus.Logout;
            } else if (
                event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.LOGIN_FAILURE ||
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                event.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
                event.eventType === EventType.SSO_SILENT_SUCCESS ||
                event.eventType === EventType.SSO_SILENT_FAILURE ||
                event.eventType === EventType.HANDLE_REDIRECT_END ||
                event.eventType === EventType.LOGOUT_SUCCESS ||
                event.eventType === EventType.LOGOUT_FAILURE
            ) {
                this._interactionStatus = InteractionStatus.None;
            }

            // Clear requiresInteraction on successful login
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                this._requiresInteraction = false;
            }

            // Notify listeners on account changes
            if (
                event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                event.eventType === EventType.LOGOUT_SUCCESS ||
                event.eventType === EventType.ACCOUNT_ADDED ||
                event.eventType === EventType.ACCOUNT_REMOVED
            ) {
                this.emit();
            }
        });
    }

    /**
     * Check if any interaction is in progress.
     * Returns false when auth is disabled.
     */
    isLoading(): boolean {
        if (this._isDisabled) return false;
        return this._interactionStatus !== InteractionStatus.None;
    }

    /**
     * Check if user is authenticated.
     * Returns true when auth is disabled (bypass mode).
     */
    isAuthenticated(): boolean {
        if (this._isDisabled) return true;
        if (this._requiresInteraction) return false;
        return this.getAccounts().length > 0;
    }

    /**
     * Get all accounts in the cache.
     * Returns empty array when auth is disabled or PCA is not yet initialized.
     */
    getAccounts(): AccountInfo[] {
        if (this._isDisabled || !this._initialized) return [];
        return this._pca?.getAllAccounts() ?? [];
    }

    /**
     * Get the active account (or first account if none is active).
     * Returns null when auth is disabled or PCA is not yet initialized.
     */
    getActiveAccount(): AccountInfo | null {
        if (this._isDisabled || !this._initialized) return null;
        return this._pca?.getActiveAccount() ?? this.getAccounts()[0] ?? null;
    }

    /**
     * Set the active account.
     * No-op when auth is disabled.
     */
    setActiveAccount(account: AccountInfo | null): void {
        if (this._isDisabled) return;
        this.pca.setActiveAccount(account);
    }

    /**
     * Trigger login via redirect.
     * No-op when auth is disabled.
     */
    async login(scopes: string[] = DEFAULT_LOGIN_SCOPES): Promise<void> {
        if (this._isDisabled) {
            console.log('[AuthService] Login skipped - auth is disabled');
            return;
        }
        await this.pca.loginRedirect({ scopes });
    }

    /**
     * Trigger login via popup.
     * Throws when auth is disabled.
     */
    async loginPopup(scopes: string[] = DEFAULT_LOGIN_SCOPES): Promise<AuthenticationResult> {
        if (this._isDisabled) {
            throw new Error('Auth is disabled. Cannot login.');
        }
        return await this.pca.loginPopup({ scopes });
    }

    /**
     * Trigger logout via redirect.
     * No-op when auth is disabled.
     */
    async logout(): Promise<void> {
        if (this._isDisabled) {
            console.log('[AuthService] Logout skipped - auth is disabled');
            return;
        }
        await this.pca.logoutRedirect();
    }

    /**
     * Trigger logout via popup.
     * No-op when auth is disabled.
     */
    async logoutPopup(): Promise<void> {
        if (this._isDisabled) {
            console.log('[AuthService] Logout skipped - auth is disabled');
            return;
        }
        await this.pca.logoutPopup();
    }

    /**
     * Acquire a token silently, falling back to interactive if needed.
     * Returns a mock token when auth is disabled.
     */
    async acquireToken(request: TokenRequest): Promise<AuthenticationResult> {
        if (this._isDisabled) {
            // Return a mock result when auth is disabled
            return {
                authority: '',
                uniqueId: 'disabled-auth-user',
                tenantId: '',
                scopes: request.scopes,
                account: null as unknown as AccountInfo, // Mock - no real account when disabled
                idToken: '',
                idTokenClaims: {},
                accessToken: 'disabled-auth-mock-token',
                fromCache: true,
                expiresOn: new Date(Date.now() + 3600000), // 1 hour from now
                tokenType: 'Bearer',
                correlationId: ''
            };
        }

        const account = request.account ?? this.getActiveAccount();
        if (!account) throw new Error('No signed-in account.');

        try {
            return await this.pca.acquireTokenSilent({ ...request, account });
        } catch (err: unknown) {
            if (err instanceof InteractionRequiredAuthError) {
                // Silent token failed — flag that interactive auth is needed so UI surfaces HRD login
                this._requiresInteraction = true;
                this.emit();
            }
            throw err;
        }
    }

    /**
     * Subscribe to account changes.
     * Returns an unsubscribe function.
     */
    onChange(listener: AuthListener): () => void {
        this.listeners.add(listener);
        listener(this.getAccounts());
        return () => this.listeners.delete(listener);
    }

    private emit(): void {
        const accts = this.getAccounts();
        for (const l of this.listeners) l(accts);
    }

    /**
     * Get the underlying MSAL instance.
     * Throws when auth is disabled.
     */
    getMsalInstance(): PublicClientApplication {
        if (this._isDisabled) {
            throw new Error('Auth is disabled. MSAL instance is not available.');
        }
        return this.pca;
    }

    /**
     * Create a FederatedAuth object that can be passed to remote apps.
     * This is the primary interface for module federation auth.
     * Works even when auth is disabled (returns mock values).
     */
    createFederatedAuth(): FederatedAuth {
        return {
            isAuthenticated: () => this.isAuthenticated(),
            isLoading: () => this.isLoading(),
            getAccount: () => this.getActiveAccount(),
            getAccounts: () => this.getAccounts(),
            login: (scopes?: string[]) => this.login(scopes),
            logout: () => this.logout(),
            acquireToken: (scopes: string[]) => this.acquireToken({ scopes }),
            onAuthStateChange: (callback: (accounts: AccountInfo[]) => void) => this.onChange(callback),
            getMsalInstance: () => this.getMsalInstance(),
            __isHostAuth: true,
            __isDisabled: this._isDisabled
        };
    }
}

// Export singleton instance
export const authService = new AuthService();
